import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Send, ArrowLeft, Flame } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useSocket } from '../hooks/useSocket';
import api from '../utils/api';

// Private, burn-after-reading messages between two party people.
// The server only shows a thread to its two participants — not the
// host — and read messages evaporate in about an hour. Everything dies
// with the party.
const POLL_MS = 5000;

const DirectMessage = ({ recipient, onBack, session }) => {
  const { user } = useAuth();
  const { socket } = useSocket();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchThread = useCallback(async () => {
    if (!session?.id || !recipient?.uid) return;
    try {
      const { data } = await api.get(`/api/sessions/${session.id}/dms/${recipient.uid}`);
      setMessages(data.messages || []);
    } catch {
      /* transient — next poll retries */
    } finally {
      setLoading(false);
    }
  }, [session?.id, recipient?.uid]);

  // Poll while open; a content-free socket ping triggers instant refetch
  useEffect(() => {
    fetchThread();
    const interval = setInterval(fetchThread, POLL_MS);
    return () => clearInterval(interval);
  }, [fetchThread]);

  useEffect(() => {
    if (!socket) return undefined;
    const onPing = (data) => {
      if (data?.to === user?.uid) fetchThread();
    };
    socket.on('dmPing', onPing);
    return () => socket.off('dmPing', onPing);
  }, [socket, user?.uid, fetchThread]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    const text = message.trim();
    if (!text || !recipient?.uid || sending) return;
    setSending(true);
    try {
      await api.post(`/api/sessions/${session.id}/dms/${recipient.uid}`, { text });
      setMessage('');
      await fetchThread();
    } catch (error) {
      /* the poll will reconcile; keep the draft so nothing is lost */
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) =>
    new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const recipientParticipant = session?.participants?.[recipient?.uid];
  const isRecipientInvisible = recipientParticipant && !recipientParticipant.isVisible;

  return (
    <div className="card h-64 sm:h-80 lg:h-96 max-h-[60vh] flex flex-col">
      {/* Header */}
      <div className="flex items-center mb-2 pb-3 border-b border-gray-600">
        <button
          onClick={onBack}
          className="text-gray-400 hover:text-white transition-colors mr-3"
          aria-label="Back to conversations"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center mr-3">
          <span className="text-white font-semibold text-sm">
            {recipient?.displayName?.charAt(0).toUpperCase() || '?'}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold text-white truncate">
            {recipient?.displayName || 'Unknown User'}
          </h2>
          <div className="flex items-center text-xs text-gray-400">
            <Flame className="w-3 h-3 mr-1 text-orange-400" />
            <span>Private — messages vanish ~1h after they're read</span>
          </div>
        </div>
      </div>

      {isRecipientInvisible && (
        <div className="bg-yellow-600/20 border border-yellow-500/30 rounded-lg px-3 py-2 mb-3 text-sm text-yellow-300">
          This user is currently invisible. They may not see your messages right away.
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 mb-4">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto mb-3"></div>
            <p className="text-gray-400 text-sm">Loading messages...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8">
            <Flame className="w-10 h-10 text-orange-400/60 mx-auto mb-3" />
            <p className="text-gray-400">No messages yet</p>
            <p className="text-gray-400 text-sm">
              Only you and {recipient?.displayName || 'this user'} can see this thread
            </p>
          </div>
        ) : (
          messages.map((msg) => {
            const isSent = msg.from === user?.uid;
            return (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${isSent ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[75%] sm:max-w-xs lg:max-w-md rounded-lg px-3 py-2 ${
                    isSent ? 'bg-indigo-600 text-white' : 'bg-gray-700 text-white'
                  }`}
                >
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-xs font-medium">
                      {isSent ? 'You' : recipient?.displayName}
                    </span>
                    <span className="text-xs opacity-70">{formatTime(msg.sentAt)}</span>
                  </div>
                  <p className="text-sm break-words" style={{ overflowWrap: 'anywhere' }}>
                    {msg.text}
                  </p>
                </div>
              </motion.div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <form onSubmit={handleSend} className="flex space-x-2">
        <input
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={`Message ${recipient?.displayName || ''}...`}
          className="input-field flex-1 min-w-0"
          maxLength={500}
        />
        <button
          type="submit"
          disabled={!message.trim() || sending}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
          aria-label="Send message"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
};

export default DirectMessage;
